import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { _get } from "../api/apiClient";
import { CustomSuccess } from "../api/CustomSuccess";
import { updateSubscriptionDetails } from "./authSlice";
import { SUBSCRIPTION } from "../utils/global";

export const fetchFISubscriptionPrivileges = createAsyncThunk(
  "fetch_fi_subscription_privileges",
  async ({ orguid, isNewPrivilage }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Fetched financial institutions subscriptions.",
        errorMessage: "Failed to fetch financial institutions subscriptions.!",
      };

      const response = await _get(
        `${orguid}/privileges?isNewPrivilage=${isNewPrivilage}`,
        {},
        { metadata: { apiName: "fetchFISubscriptionPrivileges" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      await thunkAPI.dispatch(setSubscriptionPrivileges(data));

      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchSubscriptionPrivilegesAfterPayment = createAsyncThunk(
  "fetch_fi_subscription_privileges_after_payment",
  async ({ orguid, isNewPrivilage }, thunkAPI) => {
    try {
      const config = {
        successMessage:
          "Fetched financial institutions subscriptions after payment successfully.",
        errorMessage:
          "Failed to fetch financial institutions subscriptions after payment.",
      };

      const response = await _get(
        `${orguid}/privileges?isNewPrivilage=${isNewPrivilage}`,
        {},
        { metadata: { apiName: "fetchSubscriptionPrivilegesAfterPayment" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      const [subscription = "", subscriptionName = ""] =
        data?.product_Name?.split("|") || [];

      thunkAPI.dispatch(setSubscriptionPrivileges(data?.productPrivileges));

      thunkAPI.dispatch(
        updateSubscriptionDetails({
          productId: data?.productId,
          productName: data?.productName,
          subscription: SUBSCRIPTION?.ANALYST,
          subscriptionName: subscriptionName,
          subscriptionId: data?.subscriptionId,
          productPrivileges: data?.productPrivileges,
          // privileges: data?.privileges,
        }),
      );

      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const privilegesSlice = createSlice({
  name: "privileges_slice",
  initialState: {
    list: [],
    rbacPrivileges: [],
    subscriptionPrivileges: [],
    FISubscriptionPrivileges: null,
    rootSubscriptionPrivileges: null,
  },
  reducers: {
    setRbacPrivileges: (state, action) => {
      if (typeof action.payload === "string") {
        state.rbacPrivileges = action.payload.split(",").map((p) => p.trim());
      } else if (Array.isArray(action.payload)) {
        state.rbacPrivileges = action.payload;
      }
    },
    setSubscriptionPrivileges: (state, action) => {
      if (typeof action.payload === "string") {
        state.subscriptionPrivileges = action.payload
          .split(",")
          .map((p) => p.trim());
      } else if (Array.isArray(action.payload)) {
        state.subscriptionPrivileges = action.payload;
      }
    },
    setRootSubscriptionPrivileges: (state, action) => {
      if (typeof action.payload === "string") {
        state.rootSubscriptionPrivileges = action.payload
          .split(",")
          .map((p) => p.trim());
      } else if (Array.isArray(action.payload)) {
        state.rootSubscriptionPrivileges = action.payload;
      }
    },
    resetFISubscriptionPrivileges: (state) => {
      state.FISubscriptionPrivileges = null;
    },
    clearPrivileges: (state) => {
      state.list = [];
      state.rbacPrivileges = [];
      state.subscriptionPrivileges = [];
      state.FISubscriptionPrivileges = null;
      state.rootSubscriptionPrivileges = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFISubscriptionPrivileges.pending, (_state, _action) => {
        _state.FISubscriptionPrivileges = {
          isLoading: true,
        };
      })
      .addCase(fetchFISubscriptionPrivileges.fulfilled, (_state, _action) => {
        _state.FISubscriptionPrivileges = {
          data: _action?.payload?.data,
          isLoading: false,
        };
      })
      .addCase(fetchFISubscriptionPrivileges.rejected, (_state, _action) => {
        _state.FISubscriptionPrivileges = {
          isLoading: false,
        };
      });
  },
});

export const {
  setRbacPrivileges,
  setSubscriptionPrivileges,
  setRootSubscriptionPrivileges,
  clearPrivileges,
  resetFISubscriptionPrivileges,
} = privilegesSlice.actions;
export default privilegesSlice.reducer;
